import { createContext, useContext, useMemo, useState, useEffect } from 'react';
import { ThemeProvider as MUIThemeProvider, createTheme } from '@mui/material';
import PropTypes from 'prop-types';

const ThemeContext = createContext();

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(() => {
    // Leer preferencia guardada o usar modo claro por defecto
    const savedMode = localStorage.getItem('themeMode');
    return savedMode || 'light';
  });

  useEffect(() => {
    // Guardar preferencia cuando cambie
    localStorage.setItem('themeMode', mode);
    // Actualizar el atributo del documento para CSS personalizados
    document.documentElement.setAttribute('data-theme', mode);
  }, [mode]);

  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          ...(mode === 'light'
            ? {
                // Modo claro
                primary: {
                  main: '#fc152b',
                },
                secondary: {
                  main: '#979797',
                },
                success: {
                  main: '#02c94f',
                },
                warning: {
                  main: '#ff7e07',
                },
                error: {
                  main: '#f9324f',
                },
                background: {
                  default: '#ffffff',
                  paper: '#f5f5f5',
                },
                text: {
                  primary: '#000000',
                  secondary: '#666666',
                },
              }
            : {
                // Modo oscuro
                primary: {
                  main: '#fc152b',
                },
                secondary: {
                  main: '#b0b0b0',
                },
                success: {
                  main: '#02c94f',
                },
                warning: {
                  main: '#ff7e07',
                },
                error: {
                  main: '#f9324f',
                },
                background: {
                  default: '#0e0e0e',
                  paper: '#1a1a1a',
                },
                text: {
                  primary: '#ffffff',
                  secondary: '#b0b0b0',
                },
              }),
        },
        breakpoints: {
          values: {
            mobile: 576,
            tablet: 768,
            desktop: 992,
            'desktop-xl': 1200,
          },
        },
        typography: {
          fontFamily: 'Regular',
        },
        components: {
          MuiCssBaseline: {
            styleOverrides: `
              * {
                padding: 0;
                margin: 0;
                box-sizing: border-box;
                list-style: none;
                font-family: 'Regular', sans-serif;
              }
              @font-face {
                font-family: "Regular";
                src: url(${'../../assets/Font/SFUIText-Regular.ttf'}) format("truetype");
                font-weight: normal;
                font-style: normal;
              }
              @font-face {
                font-family: "Bold";
                src: url(${'../../assets/Font/SFUIText-Bold.ttf'}) format("truetype");
                font-weight: bold;
                font-style: bold;
              }
              @font-face {
                font-family: "Heavy";
                src: url(${'../../assets/Font/SFUIText-Heavy.ttf'}) format("truetype");
                font-weight: bolder;
                font-style: bolder;
              }
              :root {
                --Red-Neftis: #fc152b;
                --Gray-border: #979797;
                --primary-color: #fc152b;
              }
            `,
          },
          MuiTypography: {
            styleOverrides: {
              root: {
                margin: 0,
                padding: 0,
                fontSize: 'inherit',
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: {
                borderRadius: 5,
                textTransform: 'capitalize',
                fontFamily: 'Bold',
                fontSize: 14,
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: 'none',
              },
            },
          },
        },
      }),
    [mode]
  );

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme }}>
      <MUIThemeProvider theme={theme}>{children}</MUIThemeProvider>
    </ThemeContext.Provider>
  );
};

ThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
